import React from 'react';
import { Sparkles, Check, Loader2, Circle, ChevronRight } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import T from 'Components/Helpers/translate';

/**
 * Shared step-render primitives for the Playbook compile drawer.
 *
 * Extracted from WorkspacePlaybookDraft so the compiled-step read-out (args,
 * prompts, filters, the tool-call header) lives in one place. Re-imported by
 * WorkspacePlaybookDraft.
 */

// "suggestReplies" -> "Suggest replies". Fallback when there's no summary/name.
export const humanizeIdentifier = (raw: string): string => {
	const spaced = raw
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[._-]+/g, ' ')
		.trim();
	if (!spaced) return raw;
	return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

export const MONO: React.CSSProperties = {
	fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
	fontSize: 12,
	wordBreak: 'break-word'
};

export const DETAIL_HEADING: React.CSSProperties = {
	fontSize: 10.5,
	fontWeight: 600,
	letterSpacing: '0.04em',
	textTransform: 'uppercase',
	color: 'var(--ws-ink-4, #8a94a6)',
	marginBottom: 6
};

// Only these shared read/report args get a localized label. Any other tool arg
// is humanized from its key — critically, T() returns the key itself when a tag
// is missing, so wrapping an arbitrary key in T() would surface the raw
// "assistant.playbook.arg.foo" string. Humanize those instead.
const KNOWN_ARG_KEYS = new Set(['fields', 'filters', 'limit', 'offset', 'replies', 'sort']);
const argLabel = (key: string): string =>
	KNOWN_ARG_KEYS.has(key) ? T(`assistant.playbook.arg.${key}`) : humanizeIdentifier(key);

// Render a leaf arg value. Scalars inline; a list of scalars as a comma list;
// anything nested falls back to compact JSON (rare for read args).
export const formatArgValue = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '—';
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	if (Array.isArray(value)) {
		if (value.every(v => typeof v === 'string' || typeof v === 'number')) return value.join(', ');
		return JSON.stringify(value);
	}
	return JSON.stringify(value);
};

// A compiled arg can carry `{{…}}` template placeholders filled in per run:
// `{{step_2_llm.field}}` is AI-written, `{{steps.x}}` / `{{trigger.y}}` carry a
// value forward from an earlier step or the trigger. Rendered as inline chips
// instead of raw braces so the user sees "an AI value goes here", not jargon.
const BINDING_RE = /\{\{\s*([^}]+?)\s*\}\}/g;
const isAiBinding = (expr: string): boolean => /llm/i.test(expr);

/**
 * Step id → the label that step is showing on screen right now.
 *
 * A binding like `{{step_1}}` used to render as the chip "Step 1", which named
 * nothing the reader could see: the run trace lists steps by name with no
 * numbers, and the drawer numbers them but titles them ("Find new leads since
 * cutoff"), so the number in the id and the number on the row are only ever
 * the same by luck — a promoted LLM step shifts one and not the other. Each
 * surface fills this with the labels it is actually rendering, so a reference
 * points at something findable.
 */
export const StepLabelContext = React.createContext<Record<string, string>>({});

const bindingLabel = (expr: string, stepLabels: Record<string, string>): string => {
	// `{{step_1}}`, `{{steps.step_1}}`, `{{step_2_llm.field}}` — the step id is
	// the first segment that names a step we know about.
	for (const part of expr.split('.')) {
		const named = stepLabels[part];
		if (named) return named;
	}
	return humanizeIdentifier(expr.split('.').pop() || expr);
};

const BINDING_PILL_BASE: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 3,
	fontSize: 11,
	fontWeight: 600,
	padding: '0 6px',
	borderRadius: 9,
	whiteSpace: 'nowrap'
};

/** Inline chip standing in for a `{{…}}` placeholder in a rendered value. */
export const BindingPill = ({ expr }: { expr: string }) => {
	const stepLabels = React.useContext(StepLabelContext);
	const ai = isAiBinding(expr);
	const name = bindingLabel(expr, stepLabels);
	const title = ai
		? T('assistant.playbook.aiValueTitle', { name }) || `AI-generated value: ${name}`
		: T('assistant.playbook.linkedValueTitle', { name }) || `Carried over from an earlier step: ${name}`;
	return (
		<span
			title={title}
			style={{
				...BINDING_PILL_BASE,
				color: ai ? 'var(--ws-info, #4a6cf7)' : 'var(--ws-ink-3, #5b6472)',
				background: ai ? 'var(--ws-info-bg, rgba(74, 108, 247, 0.1))' : 'var(--ws-bg-3, #e6e8eb)'
			}}
		>
			{ai && <Sparkles style={{ width: 10, height: 10 }} />}
			{name}
		</span>
	);
};

/** Render a value string, turning any `{{…}}` placeholders into inline chips. */
export const ValueText = ({ text }: { text: string }) => {
	const nodes: React.ReactNode[] = [];
	let last = 0;
	let key = 0;
	BINDING_RE.lastIndex = 0;
	let match: RegExpExecArray | null = BINDING_RE.exec(text);
	while (match !== null) {
		if (match.index > last) nodes.push(text.slice(last, match.index));
		nodes.push(<BindingPill key={key++} expr={match[1]} />);
		last = match.index + match[0].length;
		match = BINDING_RE.exec(text);
	}
	if (last < text.length) nodes.push(text.slice(last));
	return <>{nodes.length ? nodes : text}</>;
};

type FilterLike = { field?: unknown; comparison?: unknown; value?: unknown };
// A CRM read/report `filters` array: [{ field, comparison, value }, …]. Rendered
// as readable "field comparison value" rows instead of raw JSON.
export const isFilterArray = (value: unknown): value is FilterLike[] =>
	Array.isArray(value) &&
	value.length > 0 &&
	value.every(v => v !== null && typeof v === 'object' && 'field' in (v as object));

// An array of sentence-like strings (e.g. `replies`) — render one per line
// rather than comma-joining, which would read as a single run-on value.
const isPhraseList = (value: unknown): value is string[] =>
	Array.isArray(value) &&
	value.length > 1 &&
	value.every(v => typeof v === 'string') &&
	value.some(v => (v as string).includes(' '));

/** One "field comparison value" row inside a rendered filter list. */
export const FilterRow = ({ filter }: { filter: FilterLike }) => (
	<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline', ...MONO }}>
		<span style={{ fontWeight: 600 }}>{humanizeIdentifier(String(filter.field ?? ''))}</span>
		<span style={{ color: 'var(--ws-ink-4, #8a94a6)' }}>{String(filter.comparison ?? '')}</span>
		<span>
			<ValueText text={formatArgValue(filter.value)} />
		</span>
	</div>
);

/** A single labelled arg (e.g. Filters, Limit, Sort) inside the drilldown. */
export const ArgRow = ({ argKey, value }: { argKey: string; value: unknown }) => (
	<div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
		<span style={{ flexShrink: 0, minWidth: 64, fontSize: 12, color: 'var(--ws-ink-4, #8a94a6)' }}>
			{argLabel(argKey)}
		</span>
		<div style={{ minWidth: 0, flex: 1 }}>
			{isFilterArray(value) ? (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					{value.map((f, i) => (
						<FilterRow key={i} filter={f} />
					))}
				</div>
			) : isPhraseList(value) ? (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					{value.map((v, i) => (
						<span key={i} style={MONO}>
							<ValueText text={v} />
						</span>
					))}
				</div>
			) : (
				<span style={MONO}>
					<ValueText text={formatArgValue(value)} />
				</span>
			)}
		</div>
	</div>
);

/** A dimmed "AI instruction" block showing the prompt behind an AI-written value. */
export const PromptBlock = ({ prompt }: { prompt: unknown }) => (
	<div>
		<div style={DETAIL_HEADING}>{T('assistant.playbook.detailsPrompt') || 'AI instruction'}</div>
		<span style={{ ...MONO, whiteSpace: 'pre-wrap' }}>
			<ValueText text={formatArgValue(prompt)} />
		</span>
	</div>
);

/**
 * Capability line as a function-call signature: `tool(entity, …)`. The tool id
 * is the function, the entity is the bold first arg (what it acts on), and `…`
 * flags that parameters follow below.
 */
export const ToolCallHeader = ({
	tool,
	entity,
	hasParams
}: {
	tool: string;
	entity: string | null;
	hasParams: boolean;
}) => (
	<div style={{ ...MONO, fontSize: 13 }}>
		<span>{tool}</span>
		<span style={{ opacity: 0.5 }}>{'('}</span>
		{entity && <span style={{ fontWeight: 700 }}>{entity}</span>}
		{hasParams && <span style={{ opacity: 0.5 }}>{entity ? ', …' : '…'}</span>}
		<span style={{ opacity: 0.5 }}>{')'}</span>
	</div>
);

// One step of a Playbook/workflow run. Seeded from `run_plan` (tool + template
// args, or llm prompt); `args` becomes resolved values on `tool_use`, and
// `resultPreview` fills on `tool_result`. Persisted on the finished run's result
// message so the executed steps survive as a collapsed section (WEB-7897).
export type PlaybookRunStep = {
	id: string;
	kind: string;
	status?: 'pending' | 'running' | 'done';
	tool?: string;
	/**
	 * The tool's curated human name from the registry, sent on `run_plan`
	 * (WEB-10957). Optional: a run recorded before that shipped, or a step whose
	 * tool has left the registry, has none — those fall back to the signature.
	 */
	title?: string;
	args?: Record<string, unknown>;
	prompt?: unknown;
	resultPreview?: { count?: number; text?: string };
};

/**
 * A compiled Playbook's `llm` steps come in two kinds, and they read very
 * differently to a user:
 *
 *  - The terminal summary the compiler appends, id `step_summary` /
 *    `step_summary_N` — genuine prose written for the reader.
 *  - A PROMOTED-ARGUMENT step, id `<consumer>_llm` / `<consumer>_llm_N` — the
 *    compiler lifted one argument it could not freeze ("since yesterday at
 *    5pm") into its own step, purely to feed the tool step named by the prefix.
 *
 * The compile drawer folds the second kind into the step it feeds and marks
 * that step "Uses AI" (see `promotedByConsumer` in WorkspacePlaybookDraft). The
 * run has to do the same, or the rail shows a row called "Write summary" for a
 * step that computes a timestamp — and shows three steps where the drawer,
 * describing the same Playbook, says two.
 *
 * Keyed off the id because that is all a run carries: the drawer reads
 * `draft.decisions`, which is compile-time only, and a step's `{{…}}` bindings
 * are overwritten with resolved values as soon as it executes.
 */
const PROMOTED_SUFFIX = '_llm';
/** `_2` on the second promoted step for the same consumer; empty on the first. */
const PROMOTED_INDEX = /^_\d+$/;

// Parsed rather than matched with one regex: `^(.+)_llm(?:_\d+)?$` needs a
// greedy catch-all before the suffix, which trips the unsafe-regex lint.
const promotedConsumerId = (step: PlaybookRunStep): string | null => {
	if (step.kind !== 'llm') return null;
	const at = step.id.lastIndexOf(PROMOTED_SUFFIX);
	if (at <= 0) return null;
	const tail = step.id.slice(at + PROMOTED_SUFFIX.length);
	if (tail !== '' && !PROMOTED_INDEX.test(tail)) return null;
	return step.id.slice(0, at);
};

const stepEntity = (step: PlaybookRunStep): string | null => {
	const args = (step.args && typeof step.args === 'object' ? step.args : {}) as Record<string, unknown>;
	return typeof args.entity === 'string' || typeof args.entity === 'number' ? String(args.entity) : null;
};

/**
 * The label a run-trace row shows for a step.
 *
 * For a tool step that's the registry's curated name over the raw identifier —
 * "Read CRM (order)", not `readCRM(order)` — which is also exactly what the
 * compile drawer falls back to, so the run and the drawer finally name a step
 * the same way. The signature stays available inside the row's expansion.
 */
const runStepLabel = (step: PlaybookRunStep): string => {
	if (step.kind === 'tool' && step.tool) {
		const entity = stepEntity(step);
		const name = step.title || step.tool;
		return entity ? `${name} (${entity})` : name;
	}
	if (step.kind === 'llm') return T('assistant.playbook.stepLlm') || 'Write summary';
	if (step.kind === 'if') return T('assistant.playbook.stepIf') || 'Check condition';
	return T('assistant.playbook.stepForeach') || 'For each item';
};

/** "42 results" / the result snippet — what the row shows without being opened. */
const outcomeLabel = (step: PlaybookRunStep): string => {
	const preview = step.resultPreview;
	if (!preview) return '';
	if (typeof preview.count === 'number') {
		const unit =
			preview.count === 1
				? T('assistant.playbook.resultSingular') || 'result'
				: T('assistant.playbook.resultPlural') || 'results';
		return `${preview.count} ${unit}`;
	}
	return preview.text ?? '';
};

// The workspace scope runs on a 10px rem base, so Tailwind's rem-sized icon
// utilities (h-3.5 → 0.875rem) land at 8.75px next to 14px text and read as
// smudges. Icons here are sized in px so they stay proportionate to the row.
const ICON_SIZE: React.CSSProperties = { width: 14, height: 14, flexShrink: 0 };

const StepStatusIcon = ({ status }: { status?: PlaybookRunStep['status'] }) => {
	if (status === 'done') return <Check style={{ ...ICON_SIZE, color: 'var(--ws-ok, #2f7a4e)' }} />;
	if (status === 'running') {
		return <Loader2 className="animate-spin" style={{ ...ICON_SIZE, color: 'var(--ws-info, #3b6ba8)' }} />;
	}
	if (status === 'pending') return <Circle style={{ ...ICON_SIZE, color: 'var(--ws-ink-5, #9baabe)' }} />;
	return null;
};

/**
 * One row of the run's step rail: collapsed to a status dot, the step's
 * plain-language name, what it returned and whether it spent credits. Opening
 * it reveals the same signature / parameters / prompt the compile drawer shows.
 */
const PlaybookStepRow = ({
	step,
	promoted = [],
	isFirst,
	isLast
}: {
	step: PlaybookRunStep;
	/** Promoted-argument llm steps folded into this row — see PROMOTED_LLM_ID. */
	promoted?: PlaybookRunStep[];
	isFirst: boolean;
	isLast: boolean;
}) => {
	const [open, setOpen] = React.useState(false);
	const args = (step.args && typeof step.args === 'object' ? step.args : {}) as Record<string, unknown>;
	const entity = stepEntity(step);
	const paramEntries = Object.entries(args).filter(([k]) => k !== 'entity');
	const isTool = step.kind === 'tool' && !!step.tool;
	// A tool step that had an argument promoted is AI-driven too: the model
	// writes that argument on every run, and the credits go with it.
	const usesAi = step.kind === 'llm' || promoted.length > 0;
	const prompts = [...promoted.map(p => p.prompt), step.prompt].filter(p => p != null);
	// Nothing to reveal for a bare control-flow step — don't offer a toggle that
	// opens an empty panel.
	const hasDetail = (isTool && paramEntries.length > 0) || prompts.length > 0;
	const outcome = outcomeLabel(step);

	return (
		<div className="grid" style={{ gridTemplateColumns: '20px minmax(0, 1fr)', columnGap: 12 }}>
			{/* The connector rail: one continuous line through the whole run,
			    starting at the first dot and stopping at the last. The dot's own
			    opaque background is what punches a hole in it — a per-row line
			    inset below its dot would leave a gap at every row instead. */}
			<div className="relative flex justify-center">
				<span
					aria-hidden
					className="absolute w-px"
					style={{
						background: 'var(--ws-line-2, #d3dae4)',
						top: isFirst ? 14 : 0,
						bottom: isLast ? 'calc(100% - 14px)' : 0
					}}
				/>
				<span
					className="relative flex items-center justify-center rounded-full"
					style={{ ...ICON_SIZE, marginTop: 7, background: 'var(--ws-panel, #ffffff)' }}
				>
					<StepStatusIcon status={step.status} />
				</span>
			</div>
			<div className={cn('min-w-0', isLast ? 'pb-0.5' : 'pb-3.5')}>
				<button
					type="button"
					onClick={hasDetail ? () => setOpen(o => !o) : undefined}
					aria-expanded={hasDetail ? open : undefined}
					disabled={!hasDetail}
					className={cn(
						'-ml-1.5 flex w-full items-baseline gap-2.5 rounded px-1.5 py-0.5 text-left',
						hasDetail && 'cursor-pointer hover:bg-grey-2'
					)}
				>
					{hasDetail ? (
						<ChevronRight
							className={cn('self-center transition-transform', open && 'rotate-90')}
							style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--ws-ink-5, #9baabe)' }}
						/>
					) : (
						<span style={{ width: 12, height: 12, flexShrink: 0 }} />
					)}
					<span
						className="min-w-0 flex-1 text-body-md"
						style={{
							color: step.status === 'pending' ? 'var(--ws-ink-5, #9baabe)' : 'var(--ws-ink, #2f3947)'
						}}
					>
						{runStepLabel(step)}
					</span>
					{outcome && (
						<span className="shrink-0 text-body-sm" style={{ color: 'var(--ws-ink-4, #8a94a6)' }}>
							{outcome}
						</span>
					)}
					{/* Only the AI steps are badged. A compiled Playbook's tool steps
					    bill nothing, so "uses AI" is the exception worth flagging —
					    tagging every other row "same every run" too would repeat the
					    default on most of the list and drown the one that matters. */}
					{usesAi && (
						<span
							className="shrink-0 rounded px-1.5"
							style={{
								...MONO,
								fontSize: 10.5,
								color: 'var(--ws-info, #3b6ba8)',
								background: 'rgba(59, 107, 168, 0.1)'
							}}
							title={
								T('assistant.playbook.stepUsesAIHint') ||
								'This step calls the model, so it costs credits on every run.'
							}
						>
							{T('assistant.playbook.stepUsesAI') || 'Uses AI'}
						</span>
					)}
				</button>
				{hasDetail && open && (
					<div
						className="mt-2 flex flex-col gap-2.5 rounded-md px-3 py-2.5"
						style={{ background: 'var(--ws-soft, #edf1f5)' }}
					>
						{isTool && (
							<div className="pb-2" style={{ borderBottom: '1px solid var(--ws-line-2, #d3dae4)' }}>
								<ToolCallHeader
									tool={step.tool as string}
									entity={entity}
									hasParams={paramEntries.length > 0}
								/>
							</div>
						)}
						{paramEntries.length > 0 && (
							<div className="flex flex-col gap-1">
								{paramEntries.map(([k, v]) => (
									<ArgRow key={k} argKey={k} value={v} />
								))}
							</div>
						)}
						{prompts.map((prompt, i) => (
							<PromptBlock key={i} prompt={prompt} />
						))}
					</div>
				)}
			</div>
		</div>
	);
};

/**
 * The run's steps as a timeline rail — one collapsed row per step, naming what
 * it did, what came back and whether it spent credits; the tool signature and
 * its parameters live inside the row's expansion (WEB-10957). Shared by the
 * live in-run checklist and the "Steps" record on a finished run, so both read
 * identically.
 */
/**
 * Split a run's steps into the rows the rail draws and the promoted-argument
 * steps folded into them. Exported because the "Steps (n)" header has to count
 * what is actually rendered — a header saying 8 over six rows is its own bug.
 */
export const foldPromotedSteps = (
	steps: PlaybookRunStep[]
): { visible: PlaybookRunStep[]; promotedByConsumer: Record<string, PlaybookRunStep[]> } => {
	const byId = new Set(steps.map(s => s.id));
	const promotedByConsumer: Record<string, PlaybookRunStep[]> = {};
	const visible: PlaybookRunStep[] = [];
	for (const step of steps) {
		const consumer = promotedConsumerId(step);
		// Only fold when the step it names is actually in this run. A
		// hand-authored workflow whose llm step happens to end in `_llm` keeps
		// its own row rather than vanishing.
		if (consumer && byId.has(consumer)) {
			(promotedByConsumer[consumer] ??= []).push(step);
		} else {
			visible.push(step);
		}
	}
	return { visible, promotedByConsumer };
};

export const PlaybookStepList = ({ steps }: { steps: PlaybookRunStep[] }) => {
	const { visible, promotedByConsumer } = React.useMemo(() => foldPromotedSteps(steps), [steps]);

	// Publish what each row is called so a `{{step_1}}` inside a prompt below
	// renders as that row's name instead of an id the list never shows.
	//
	// Deliberately only the VISIBLE rows. A folded step has no row to point at,
	// and its generic "Write summary" label would name the wrong thing entirely
	// — left out, `bindingLabel` falls back to humanising the field the binding
	// reads, so `{{step_1_llm.cutoffTimestamp}}` renders as "Cutoff timestamp",
	// which is what the value actually is.
	const labels = React.useMemo(() => Object.fromEntries(visible.map(s => [s.id, runStepLabel(s)])), [visible]);

	return (
		<StepLabelContext.Provider value={labels}>
			<div className="flex flex-col">
				{visible.map((step, i) => (
					<PlaybookStepRow
						key={step.id}
						step={step}
						promoted={promotedByConsumer[step.id]}
						isFirst={i === 0}
						isLast={i === visible.length - 1}
					/>
				))}
			</div>
		</StepLabelContext.Provider>
	);
};
